import { Star } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { AgentRating } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  useDeleteAgentRating,
  usePutAgentRating,
} from "@/features/agents/hooks/useAgents";
import { cn } from "@/lib/utils";

interface Props {
  agentId: string;
  agentName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingRating?: AgentRating | null;
}

const MAX_COMMENT = 2000;

export function RateAgentDialog({
  agentId,
  agentName,
  open,
  onOpenChange,
  existingRating,
}: Props) {
  const put = usePutAgentRating(agentId);
  const del = useDeleteAgentRating(agentId);
  const [stars, setStars] = useState<number>(existingRating?.rating ?? 0);
  const [hoverStars, setHoverStars] = useState<number | null>(null);
  const [comment, setComment] = useState<string>(existingRating?.comment ?? "");

  useEffect(() => {
    if (open) {
      setStars(existingRating?.rating ?? 0);
      setComment(existingRating?.comment ?? "");
      setHoverStars(null);
    }
  }, [open, existingRating]);

  async function handleSubmit() {
    if (stars < 1 || stars > 5) {
      toast.error("Selecione de 1 a 5 estrelas");
      return;
    }
    try {
      await put.mutateAsync({
        rating: stars,
        comment: comment.trim() || undefined,
      });
      toast.success("Avaliação salva");
      onOpenChange(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleDelete() {
    if (!existingRating) return;
    try {
      await del.mutateAsync();
      toast.success("Avaliação removida");
      onOpenChange(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  const display = hoverStars ?? stars;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Avaliar {agentName}</DialogTitle>
          <DialogDescription>
            Sua avaliação ajuda outros usuários a descobrir bons agentes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <Label>Nota</Label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setStars(n)}
                  onMouseEnter={() => setHoverStars(n)}
                  onMouseLeave={() => setHoverStars(null)}
                  aria-label={`${n} estrelas`}
                  className="rounded-md p-1 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/40"
                >
                  <Star
                    className={cn(
                      "h-7 w-7",
                      n <= display
                        ? "fill-accent-500 text-accent-500"
                        : "text-ink-300",
                    )}
                  />
                </button>
              ))}
              <span className="ml-2 text-sm font-semibold text-ink-700">
                {display > 0 ? `${display}/5` : "—"}
              </span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rating-comment">Comentário (opcional)</Label>
            <Textarea
              id="rating-comment"
              rows={4}
              value={comment}
              maxLength={MAX_COMMENT}
              onChange={(e) => setComment(e.target.value)}
              placeholder="O que esse agente acertou ou pode melhorar?"
            />
            <p className="text-right text-xs text-ink-500">
              {comment.length}/{MAX_COMMENT}
            </p>
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <div>
            {existingRating ? (
              <Button
                type="button"
                variant="ghost"
                onClick={handleDelete}
                disabled={del.isPending}
                className="text-danger-700 hover:text-danger-700"
              >
                Remover avaliação
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={stars < 1 || put.isPending}
            >
              Salvar avaliação
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
